import { Button, Card, EmptyState, Skeleton } from '@personachat/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Image, MessagesSquare } from 'lucide-react';
import { useState } from 'react';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import type { components } from '../../api/schema';
import { DataTable, type Colonne } from '../../components/DataTable';
import { LectureConversation } from '../privacy/Confidentialite';
import { ErreurChargement, Section } from '../../components/bits';
import { formatDate, formatDateTime, formatNombre } from '../../lib/format';

type Fiche = components['schemas']['UserDetail'];

/**
 * Onglet Activité : compteurs, conversations (titres ; leur lecture demande
 * un motif, inscrit au journal), images et vidéos générées.
 */
export function ActivityTab({ eleve }: { eleve: Fiche }) {
  const [pageConv, setPageConv] = useState(1);
  const [genre, setGenre] = useState<'IMAGE' | 'VIDEO'>('IMAGE');
  const [pageMedia, setPageMedia] = useState(1);
  const [lue, setLue] = useState<{ id: string; titre: string } | null>(null);

  const activite = useQuery({
    queryKey: ['eleve', eleve.id, 'activite'],
    queryFn: () => lire(api.GET('/api/admin/users/{id}/activity', { params: { path: { id: eleve.id } } })),
  });
  const conversations = useQuery({
    queryKey: ['eleve', eleve.id, 'conversations', pageConv],
    queryFn: () => lire(api.GET('/api/admin/users/{id}/conversations', { params: { path: { id: eleve.id }, query: { page: pageConv, limit: 10 } } })),
    placeholderData: keepPreviousData,
  });
  const medias = useQuery({
    queryKey: ['eleve', eleve.id, 'media', genre, pageMedia],
    queryFn: () => lire(api.GET('/api/admin/users/{id}/media', { params: { path: { id: eleve.id }, query: { kind: genre, page: pageMedia, limit: 12 } } })),
    placeholderData: keepPreviousData,
  });

  type LigneConv = NonNullable<typeof conversations.data>['conversations'][number];
  const colonnes: Colonne<LigneConv>[] = [
    {
      id: 'titre',
      header: 'Conversation',
      cell: (c) => (
        <div>
          <div className="cell-main">{c.title || 'Sans titre'}</div>
          <div className="cell-sub">
            {c.personality?.name ?? 'Coach supprimé'}
            {c.project && ` · ${c.project.name}`}
            {c.isArchived && ' · archivée'}
          </div>
        </div>
      ),
    },
    { id: 'messages', header: 'Messages', align: 'right', cell: (c) => <span className="num">{formatNombre(c.messagesCount)}</span> },
    { id: 'date', header: 'Dernier message', cell: (c) => <span className="nowrap">{formatDateTime(c.lastMessageAt ?? c.createdAt)}</span> },
  ];

  const compteurs = activite.data?.counts;
  const tuiles = [
    { label: 'Conversations', valeur: compteurs?.conversations },
    { label: 'Messages envoyés', valeur: compteurs?.messagesSent },
    { label: 'Images', valeur: compteurs?.images },
    { label: 'Vidéos', valeur: compteurs?.videos },
  ];

  return (
    <>
      {activite.isError ? (
        <ErreurChargement erreur={activite.error} onReessayer={() => void activite.refetch()} />
      ) : (
        <div className="kpis kpis--compact">
          {tuiles.map((t) => (
            <Card key={t.label} className="kpi">
              <div className="kpi__label">{t.label}</div>
              <div className="kpi__value">{t.valeur === undefined ? <Skeleton width={50} height={24} /> : formatNombre(t.valeur)}</div>
            </Card>
          ))}
        </div>
      )}
      {compteurs && compteurs.failedVideos > 0 && (
        <p className="note">
          {compteurs.failedVideos} vidéo{compteurs.failedVideos > 1 ? 's' : ''} en échec (crédits remboursés automatiquement).
        </p>
      )}

      <Section title="Conversations">
        <p className="note" style={{ marginTop: 0 }}>
          Cliquez une conversation pour la lire : un motif est demandé et inscrit au journal.
        </p>
        {conversations.isError ? (
          <ErreurChargement erreur={conversations.error} onReessayer={() => void conversations.refetch()} />
        ) : (
          <DataTable
            libelle="Conversations"
            colonnes={colonnes}
            lignes={conversations.data?.conversations}
            cleLigne={(c) => c.id}
            chargement={conversations.isFetching && !conversations.data}
            pagination={conversations.data ? { page: conversations.data.pagination.page, pages: conversations.data.pagination.pages, total: conversations.data.pagination.total } : undefined}
            onPage={setPageConv}
            onOuvrir={(c) => setLue({ id: c.id, titre: c.title || 'Conversation sans titre' })}
            vide={<EmptyState icon={<MessagesSquare size={24} aria-hidden="true" />} title="Aucune conversation" />}
          />
        )}
        {lue && <LectureConversation eleveId={eleve.id} conversationId={lue.id} titre={lue.titre} onClose={() => setLue(null)} />}
      </Section>

      <Section
        title="Images et vidéos"
        actions={
          <div className="segmented" role="group" aria-label="Type de média">
            {(['IMAGE', 'VIDEO'] as const).map((g) => (
              <button
                key={g}
                type="button"
                aria-pressed={genre === g}
                onClick={() => {
                  setGenre(g);
                  setPageMedia(1);
                }}
              >
                {g === 'IMAGE' ? 'Images' : 'Vidéos'}
              </button>
            ))}
          </div>
        }
      >
        {medias.isError ? (
          <ErreurChargement erreur={medias.error} onReessayer={() => void medias.refetch()} />
        ) : !medias.data ? (
          <Skeleton height={140} />
        ) : medias.data.media.length === 0 ? (
          <EmptyState icon={<Image size={24} aria-hidden="true" />} title={genre === 'IMAGE' ? 'Aucune image' : 'Aucune vidéo'} />
        ) : (
          <>
            <div className="media-grid">
              {medias.data.media.map((m) => (
                <a key={m.id} className="media-grid__item" href={m.url ?? undefined} target="_blank" rel="noreferrer" title={m.prompt ?? undefined}>
                  {m.kind === 'IMAGE' ? (
                    m.url ? <img src={m.url} alt={m.prompt ? `Image : ${m.prompt.slice(0, 80)}` : 'Image générée'} loading="lazy" /> : null
                  ) : m.url ? (
                    // #t=0.1 : Safari n'affiche pas la première image d'une vidéo sans position de lecture.
                    <video src={`${m.url}#t=0.1`} preload="metadata" muted playsInline aria-label="Vidéo générée" />
                  ) : null}
                  <span className="media-grid__caption">
                    {formatDate(m.createdAt)}
                    {m.durationSec ? ` · ${m.durationSec} s` : ''}
                  </span>
                </a>
              ))}
            </div>
            {medias.data.pagination.pages > 1 && (
              <div className="row" style={{ marginTop: 10 }}>
                <Button size="sm" onClick={() => setPageMedia((p) => p - 1)} disabled={pageMedia <= 1}>
                  Précédentes
                </Button>
                <span className="muted small">
                  Page {pageMedia} sur {medias.data.pagination.pages}
                </span>
                <Button size="sm" onClick={() => setPageMedia((p) => p + 1)} disabled={pageMedia >= medias.data.pagination.pages}>
                  Suivantes
                </Button>
              </div>
            )}
          </>
        )}
      </Section>
    </>
  );
}
